import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: "01",
    title: "Medical Assessment",
    description: "Complete our brief, secure health assessment. Our physicians review your medical history and verify you're a strong candidate for the program.",
    time: "~5 min"
  },
  {
    number: "02",
    title: "Physician Consultation",
    description: "Meet 1-on-1 with our medical team. Together you'll map your exact blueprint, discuss prescription options, and set aggressive, measurable targets.",
    time: "~30 min"
  },
  {
    number: "03",
    title: "The 6-Week Sprint",
    description: "Access your custom protocol immediately. If prescribed, your medication is seamlessly fulfilled and shipped directly to your door.",
    time: "6 weeks"
  },
];

const HowItWorks = () => (
  <section id="how-it-works" className="py-20 md:py-32 bg-stone-950 text-white relative overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_100%,rgba(217,119,6,0.08),transparent)]" />
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-700 to-transparent" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="text-center mb-16 md:mb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-block px-5 py-2 rounded-full bg-stone-800 border border-stone-700 text-amber-400 text-sm font-bold uppercase tracking-widest mb-6">
            The Process
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-serif mb-6">
            Execute With Precision
          </h2>
          <p className="text-stone-400 text-xl md:text-2xl font-light max-w-2xl mx-auto leading-relaxed">
            A streamlined, medical-grade path to your transformation. We provide the map. You walk the path.
          </p>
        </motion.div>
      </div>

      <div className="relative">
        <div className="hidden lg:block absolute top-16 left-[16.67%] right-[16.67%] h-px">
          <div className="w-full h-full bg-gradient-to-r from-transparent via-stone-700 to-transparent" />
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"
            initial={{ x: '-100%' }}
            whileInView={{ x: '100%' }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-10">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.7 }}
              className="group relative"
            >
              <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
                <div className="relative mb-8 z-10">
                  <div className="w-16 h-16 rounded-2xl bg-stone-900 border border-stone-700 group-hover:border-amber-500/50 flex items-center justify-center transition-all duration-500 group-hover:bg-stone-800">
                    <span className="text-amber-400 font-black text-xl tracking-tight">{step.number}</span>
                  </div>
                </div>
                <div className="mb-4 flex items-center gap-2 lg:justify-start justify-center">
                  <span className="text-xs font-black uppercase tracking-widest text-stone-500 border border-stone-800 px-2.5 py-1 rounded-full">
                    {step.time}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight text-stone-50 font-serif">{step.title}</h3>
                <p className="text-lg md:text-xl text-stone-400 font-light leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default HowItWorks;